import { useEffect, type FC } from 'react';

import { useCms, useContent } from '@eneco/cms';
import { useCustomerPublicPostCustomerOrderConfirmation } from '@eneco/dc/hooks';
import { useRouter } from '@eneco/next';

import type { ContractConfirmationContent } from './contract-confirmation-content.types';

/**
 * `ContractConfirmation` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors the legacy Sitecore lane (`libs/sitecore/components/content/src/ContractConfirmation/
 * ContractConfirmation.tsx`): a pure redirect controller, renders nothing. Reads the `id` query
 * param, confirms the order, then routes to `successUrl` or `failedUrl` depending on the outcome
 * (or straight to `failedUrl` when there's no `id` at all).
 */
const ContractConfirmation: FC = () => {
  const { adapter } = useCms();
  const content = useContent<ContractConfirmationContent>();
  const { isSuccess, isError, send } = useCustomerPublicPostCustomerOrderConfirmation();
  const router = useRouter();

  const failedHref = adapter.toLink(content.failedUrl)?.href;
  const successHref = adapter.toLink(content.successUrl)?.href;

  useEffect(() => {
    const orderId = new URLSearchParams(window.location.search).get('id');

    if (orderId) {
      send({ orderId });
    } else if (failedHref) {
      router.replace(failedHref);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isSuccess && successHref) {
      router.replace(successHref);
    }
    if (isError && failedHref) {
      router.replace(failedHref);
    }
  }, [isSuccess, isError, router, successHref, failedHref]);

  return null;
};

export default ContractConfirmation;
